'use client'

// https://21st.dev/danielpetho/typewriter/default

import { m, Variants } from 'motion/react'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

interface TypewriterProps {
  text: string | string[]
  speed?: number
  initialDelay?: number
  waitTime?: number
  deleteSpeed?: number
  loop?: boolean
  className?: string
  showCursor?: boolean
  hideCursorOnType?: boolean
  cursorChar?: string | React.ReactNode
  cursorAnimationVariants?: {
    initial: Variants['initial']
    animate: Variants['animate']
  }
  cursorClassName?: string
}

// Where the cycle is: typing a text out, holding it whole, deleting it,
// or waiting on the empty line before the next one. The text on screen is
// always the first `chars` characters of the current text.
type Phase = 'typing' | 'holding' | 'deleting' | 'waiting'

interface Cycle {
  phase: Phase
  textIndex: number
  chars: number
}

const toList = (text: string | string[]) =>
  Array.isArray(text) ? text : [text]

const begin = (texts: string[], textIndex: number): Cycle => ({
  phase: (texts[textIndex]?.length ?? 0) > 0 ? 'typing' : 'holding',
  textIndex,
  chars: 0,
})

interface Timing {
  speed: number
  deleteSpeed: number
  waitTime: number
  loop: boolean
}

// The cycle's next beat and how long until it lands, or null where it
// rests: a lone text held whole, or the last text gone when not looping.
function nextBeat(
  c: Cycle,
  texts: string[],
  t: Timing,
): { after: number; next: Cycle } | null {
  const length = texts[c.textIndex]?.length ?? 0
  switch (c.phase) {
    case 'typing': {
      const chars = c.chars + 1
      return {
        after: t.speed,
        next: { ...c, chars, phase: chars >= length ? 'holding' : 'typing' },
      }
    }
    case 'holding':
      // The text grew while held (a consumer swapped it): type the rest.
      if (c.chars < length) return nextBeat({ ...c, phase: 'typing' }, texts, t)
      return texts.length > 1
        ? { after: t.waitTime, next: { ...c, phase: 'deleting' } }
        : null
    case 'deleting': {
      const chars = c.chars - 1
      return {
        after: t.deleteSpeed,
        next: { ...c, chars, phase: chars <= 0 ? 'waiting' : 'deleting' },
      }
    }
    case 'waiting':
      if (c.textIndex === texts.length - 1 && !t.loop) return null
      return {
        after: t.waitTime,
        next: begin(texts, (c.textIndex + 1) % texts.length),
      }
  }
}

const Typewriter = ({
  text,
  speed = 50,
  initialDelay = 0,
  waitTime = 2000,
  deleteSpeed = 30,
  loop = true,
  className,
  showCursor = true,
  hideCursorOnType = false,
  cursorChar = '|',
  cursorClassName = 'ml-1',
  cursorAnimationVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.01,
        repeat: Infinity,
        repeatDelay: 0.4,
        repeatType: 'reverse',
      },
    },
  },
}: TypewriterProps) => {
  const [cycle, setCycle] = useState<Cycle>(() => begin(toList(text), 0))

  const texts = toList(text)
  const displayText = (texts[cycle.textIndex] ?? '').slice(0, cycle.chars)

  // `text` is a dependency so a consumer swapping it mid-run has the next
  // beat planned against the new texts.
  useEffect(() => {
    const beat = nextBeat(cycle, toList(text), {
      speed,
      deleteSpeed,
      waitTime,
      loop,
    })
    if (!beat) return
    const advance = () => setCycle(beat.next)
    let timer: ReturnType<typeof setTimeout> | undefined
    // Each text starts typing after the initial delay.
    const timeout =
      cycle.phase === 'typing' && cycle.chars === 0
        ? setTimeout(() => {
            timer = setTimeout(advance, beat.after)
          }, initialDelay)
        : setTimeout(advance, beat.after)
    return () => {
      clearTimeout(timeout)
      if (timer) clearTimeout(timer)
    }
  }, [text, cycle, speed, deleteSpeed, waitTime, initialDelay, loop])

  return (
    <div className={`inline tracking-tight whitespace-pre-wrap ${className}`}>
      <span>{displayText}</span>
      {showCursor && (
        <m.span
          variants={cursorAnimationVariants}
          className={cn(
            cursorClassName,
            hideCursorOnType && cycle.phase !== 'holding' ? 'hidden' : '',
          )}
          initial="initial"
          animate="animate"
        >
          {cursorChar}
        </m.span>
      )}
    </div>
  )
}

export { Typewriter }
